import { useContext, useMemo } from 'react';

import { LocationContext } from 'common/containers/RouterContainer';
import { calculateQueryParams } from 'common/subdomain/admin/v2/utils';
import { type Filter, validateFilter } from 'common/views/filters';

import type { View } from 'common/api/endpoints/view';
import type { QueryParams } from 'common/subdomain/admin/v2/containers/IdeaListContainer';

/**
 * Custom hook that centralizes query parameter calculation using LocationContext and selectedView.
 * This ensures consistent query parameters across all components and prevents Redux caching issues.
 *
 * @param selectedView - The currently selected view, or null if no view is selected
 */
export function useQueryParams(selectedView: View | null | undefined): QueryParams {
  const location = useContext(LocationContext);

  // Memoization here is important to prevent unnecessary re-renders of components that use this hook
  return useMemo(
    () =>
      calculateQueryParams({
        groupBy: selectedView?.groupBy,
        groupURLName: location.query.group,
        search: location.query.search,
        viewURLName: selectedView?.urlName ?? location.query.view,
        sort: selectedView?.sort,
        filtering: selectedView
          ? {
              filters: selectedView.filters.filter((filter: Filter) => validateFilter(filter)),
              filtersOperator: selectedView.filtersOperator,
            }
          : undefined,
      }),
    [location.query.group, location.query.search, location.query.view, selectedView],
  );
}
